/**
 * AI Insights Service (mocked)
 *
 * Generates contextual insights for financial metrics.
 * When the Claude API integration is ready, replace the mock
 * implementation with real API calls using the persona's systemPrompt.
 */

import type { FinancialData } from '@/components/financial/financial-config'
import type { TrendResult } from '@/lib/services/trend-analysis.service'

export interface AIInsight {
  metric: string
  metricLabel: string
  summary: string
  attentionPoints: string[]
  suggestions: string[]
  generatedAt: string
}

const MOCK_INSIGHTS: Record<string, Omit<AIInsight, 'metric' | 'metricLabel' | 'generatedAt'>> = {
  receitaBruta: {
    summary: 'A Receita Bruta acumulada no período está abaixo do ritmo esperado para atingir o PROA anual. Comparando com o mesmo período do ano anterior, observa-se uma retração que pode estar relacionada à sazonalidade do setor ou à perda de volume em clientes-chave.',
    attentionPoints: [
      'Concentração de receita em poucos clientes aumenta o risco de volatilidade',
      'O ritmo de faturamento nos últimos 2 meses desacelerou em relação ao início do ano',
      'Sazonalidade histórica indica que o pico de receita ocorre entre Maio e Julho',
    ],
    suggestions: [
      'Antecipar faturamento de projetos já aprovados para melhorar o fluxo do trimestre',
      'Revisar pipeline de novos negócios e priorizar fechamentos de curto prazo',
      'Avaliar se há jobs encarteirados que podem ser convertidos em receita ainda neste mês',
    ],
  },
  faturamento: {
    summary: 'O Faturamento Total acompanha a tendência da Receita Bruta. A diferença entre Faturamento e Receita pode indicar ajustes em impostos ou devoluções que merecem atenção.',
    attentionPoints: [
      'Gap entre faturamento e receita bruta pode indicar aumento de deduções',
      'Volume de notas fiscais emitidas está estável, mas o ticket médio caiu',
    ],
    suggestions: [
      'Investigar se o aumento de deduções está relacionado a renegociações comerciais',
      'Focar em projetos de maior valor agregado para recuperar o ticket médio',
    ],
  },
  lucroLiquido: {
    summary: 'O Lucro Líquido está sob pressão. A combinação de receita abaixo do esperado com custos de folha estáveis cria um efeito tesoura que reduz as margens. O ajuste aplicado (foto do presente) mostra um cenário mais realista.',
    attentionPoints: [
      'Margem líquida caiu em relação ao período anterior',
      'Custos fixos (folha) representam parcela crescente da receita',
      'Projeção bottom-up indica risco de não atingir o PROA de lucro',
    ],
    suggestions: [
      'Reavaliar alocação de equipe em projetos de baixa margem',
      'Negociar antecipação de pagamentos com clientes para melhorar fluxo de caixa',
      'Considerar postergar investimentos não essenciais para proteger a margem',
    ],
  },
  folha: {
    summary: 'Os custos de Folha estão dentro do orçamento PROA, mas sua participação relativa na receita aumentou devido à retração do faturamento. A folha é o maior custo fixo da operação.',
    attentionPoints: [
      'Relação folha/receita piorou em comparação ao período anterior',
      'Projeções indicam que a folha ficará próxima do limite PROA no acumulado anual',
    ],
    suggestions: [
      'Otimizar alocação de equipe para maximizar horas produtivas por colaborador',
      'Avaliar terceirização pontual em picos de demanda ao invés de novas contratações',
    ],
  },
  custosServicoPrestado: {
    summary: 'Os Custos de Serviços Prestados (terceiros) estão alinhados com o volume de projetos executados. Custos já contratados mas não lançados podem impactar os próximos meses.',
    attentionPoints: [
      'Custos comprometidos (não pagos) adicionam pressão ao resultado futuro',
      'Fornecedores com maior volume não necessariamente oferecem os melhores preços',
    ],
    suggestions: [
      'Consolidar fornecedores para obter melhores condições comerciais',
      'Renegociar contratos de fornecedores recorrentes com base no volume anual',
    ],
  },
  despesas: {
    summary: 'As Despesas Operacionais estão controladas e dentro da meta PROA. Manutenção deste ritmo é importante para proteger a margem em cenário de receita abaixo do esperado.',
    attentionPoints: [
      'Algumas categorias de despesa cresceram acima da inflação',
      'Despesas com tecnologia e infraestrutura tendem a ser sazonais no Q4',
    ],
    suggestions: [
      'Manter disciplina de aprovação para novas despesas até revisão de meio de ano',
      'Identificar despesas recorrentes que podem ser otimizadas ou eliminadas',
    ],
  },
  impostos: {
    summary: 'Os Impostos (PIS + COFINS + ISS) estão proporcionais ao faturamento. Variações significativas podem indicar mudanças na composição de receita por tipo de serviço.',
    attentionPoints: [
      'Alterações na alíquota efetiva podem indicar mudança no mix de serviços',
    ],
    suggestions: [
      'Verificar se a composição de receita (promo vs digital) afeta a carga tributária',
      'Consultar planejamento tributário para otimização fiscal no próximo trimestre',
    ],
  },
  receitaFinanceira: {
    summary: 'A Receita Financeira contribui positivamente para o resultado. Em cenário de juros altos, manter capital aplicado continua sendo vantajoso.',
    attentionPoints: [
      'Receita financeira depende do volume de caixa disponível para aplicação',
      'Queda nos juros pode reduzir essa linha nos próximos trimestres',
    ],
    suggestions: [
      'Otimizar prazos de recebimento para maximizar capital aplicado',
      'Avaliar produtos financeiros com melhor relação risco/retorno',
    ],
  },
}

/**
 * Generate a mock AI insight for a given financial metric.
 * In production, this will call the Claude API with the persona's systemPrompt.
 */
export function generateInsight(
  metric: string,
  metricLabel: string,
  _data?: FinancialData,
  _trend?: TrendResult | null,
): AIInsight {
  const mock = MOCK_INSIGHTS[metric] ?? {
    summary: `Análise da métrica "${metricLabel}" será disponibilizada em breve quando a integração com IA estiver ativa.`,
    attentionPoints: ['Dados insuficientes para gerar pontos de atenção automatizados.'],
    suggestions: ['Configure a integração com a API do Claude para habilitar insights inteligentes.'],
  }

  return {
    metric,
    metricLabel,
    ...mock,
    generatedAt: new Date().toISOString(),
  }
}
